import { useQuery } from "@tanstack/react-query";
import { useApi } from "@ncp/api-client";
import { useSave } from "@ncp/ui";
import type { BaseBody, DefinitionSummary } from "../generic/api";

export type RuleKind = "Guard" | "Validation" | "Eligibility" | "Condition" | "Computation";
export const RULE_KINDS: RuleKind[] = ["Guard", "Eligibility", "Condition", "Validation", "Computation"];

export type DecisionOp = "any" | "eq" | "neq" | "gt" | "gte" | "lt" | "lte" | "between" | "oneOf" | "contains" | "empty" | "filled";
export const DECISION_OPS: DecisionOp[] = ["any", "eq", "neq", "gt", "gte", "lt", "lte", "between", "oneOf", "contains", "empty", "filled"];

export interface DecisionInput { key: string; path: string; label?: string | null; labelAr?: string | null; type?: string | null }
export interface DecisionCell { op: DecisionOp; value?: unknown }
export interface DecisionRow { cells: Record<string, DecisionCell>; output: unknown; note?: string | null }
export interface DecisionTable { inputs: DecisionInput[]; rows: DecisionRow[]; default: unknown }

export interface RuleTestCase { name: string; input: Record<string, unknown>; expected: unknown }

export interface RuleBody extends BaseBody {
  kind: RuleKind;
  logic: unknown;
  table?: DecisionTable | null;
  contextSchema?: unknown;
  testCases: RuleTestCase[];
  explanation?: string | null;
  explanationAr?: string | null;
  serviceKey?: string | null;
}

export interface RuleSummary extends DefinitionSummary {
  kind?: RuleKind;
  serviceKey?: string | null;
  isTable?: boolean;
  lastTestPassed?: number | null;
  lastTestTotal?: number | null;
}

export interface RuleField { path: string; label: string; labelAr?: string | null; type: string; options?: string[] | null }
export interface RuleTestResult { name: string; passed: boolean; expected: unknown; actual: unknown; error?: string | null }
export interface RuleTestRun { passed: number; total: number; results: RuleTestResult[]; logic: unknown; variables: string[] }
export interface RuleCaseEvaluation { caseNumber: string; result: unknown; holds: boolean; error?: string | null; values: Record<string, unknown> }

export function newRuleBody(name: string, nameAr: string | null, extra: Record<string, string>): RuleBody {
  const table = extra.mode !== "logic";
  return {
    name, nameAr, description: null, descriptionAr: null, tags: [],
    kind: (extra.kind as RuleKind) || "Guard",
    logic: true,
    table: table ? { inputs: [{ key: "in1", path: "status", label: "Case status", type: "text" }], rows: [{ cells: {}, output: true }], default: false } : null,
    testCases: [], explanation: null, explanationAr: null, serviceKey: null,
  };
}

/** A context object with every referenced path set to a placeholder value, for a new test case. */
export function inputSkeleton(paths: string[], fields: RuleField[]): Record<string, unknown> {
  const root: Record<string, unknown> = {};
  for (const path of paths) {
    const type = fields.find((f) => f.path === path)?.type;
    const parts = path.split(".");
    let node = root;
    parts.forEach((p, i) => {
      if (i === parts.length - 1) node[p] = type === "number" ? 0 : type === "boolean" ? false : "";
      else node = (node[p] ??= {}) as Record<string, unknown>;
    });
  }
  return root;
}

export function useRuleFields(serviceKey?: string | null) {
  const api = useApi();
  return useQuery({ queryKey: ["platform", "rules", "fields", serviceKey ?? ""], queryFn: () => api.get<RuleField[]>("/api/platform/rules/fields", serviceKey ? { service: serviceKey } : undefined), staleTime: 60_000 });
}

// Tests and case checks store nothing; the designer shows their failure beside the run, with its cause (report: "silent").
export function useRuleTest() {
  const api = useApi();
  return useSave({ mutationFn: (body: RuleBody) => api.post<RuleTestRun>("/api/platform/rules/test", { body }), report: "silent" });
}

export function useRuleOnCase() {
  const api = useApi();
  return useSave({ mutationFn: (input: { body: RuleBody; caseId: string }) => api.post<RuleCaseEvaluation>("/api/platform/rules/evaluate", input), report: "silent" });
}
